import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, MapPin, Search, CheckCircle2, AlertTriangle } from "lucide-react";

// value: { postcode, town, county, lat, lng, source }
// onChange(next) — parent stores the resolved location. Coordinates are ONLY set from a
// successful postcode lookup; a manual town entry clears coordinates (never invents them).
export function PostcodeField({ value = {}, onChange, testid = "postcode" }) {
  const [pc, setPc] = useState(value.postcode || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resolved, setResolved] = useState(
    value.lat != null && value.lng != null ? { lat: value.lat, lng: value.lng, suggested_town: value.town, admin_county: value.county, postcode: value.postcode } : null
  );
  const [town, setTown] = useState(value.town || "");

  const lookup = async () => {
    setBusy(true); setError(""); setResolved(null);
    try {
      const r = await api.get(`/geo/postcode`, { params: { postcode: pc } });
      setResolved(r.data);
      setTown(r.data.suggested_town || "");
      // Do NOT save yet — the user confirms/corrects the town first.
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Lookup failed. You can enter your town manually below.");
    } finally { setBusy(false); }
  };

  const confirm = () => {
    if (!resolved) return;
    onChange?.({ postcode: resolved.postcode, town: (town || "").trim() || resolved.suggested_town || "",
      county: resolved.admin_county || "", lat: resolved.lat, lng: resolved.lng, source: "postcode" });
  };

  const saveManual = () => {
    // Manual fallback: keep the town the user typed, but NO coordinates (never retained from an old postcode).
    onChange?.({ postcode: (pc || "").trim().toUpperCase(), town: (town || "").trim(),
      county: "", lat: null, lng: null, source: "manual" });
  };

  const confirmed = value.source && (value.town || value.lat != null);

  return (
    <div data-testid={`${testid}-field`} className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-3">
      <Label className="flex items-center gap-1.5 text-sm font-semibold text-zinc-700"><MapPin className="h-4 w-4 text-emerald-600" /> Postcode lookup</Label>
      <p className="mt-0.5 text-xs text-zinc-500">Enter a UK postcode to set your location and map pin. This is a postcode lookup, not a street address. You can adjust the town after.</p>
      <div className="mt-2 flex gap-2">
        <Input data-testid={`${testid}-input`} value={pc} onChange={(e) => setPc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), lookup())} placeholder="e.g. GU15 3PT" maxLength={10} className="h-10 uppercase" />
        <Button type="button" data-testid={`${testid}-lookup`} onClick={lookup} disabled={busy || !pc.trim()} className="h-10 rounded-full bg-emerald-600 hover:bg-emerald-700">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="mr-1 h-4 w-4" /> Look up</>}
        </Button>
      </div>

      {error && (
        <div data-testid={`${testid}-error`} className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
          <p className="flex items-center gap-1 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> {error}</p>
          <div className="mt-2">
            <Label className="text-xs">Enter your town manually (no map pin will be set)</Label>
            <div className="mt-1 flex gap-2">
              <Input data-testid={`${testid}-manual-town`} value={town} onChange={(e) => setTown(e.target.value)} placeholder="e.g. Camberley" className="h-9" />
              <Button type="button" size="sm" data-testid={`${testid}-manual-save`} onClick={saveManual} disabled={!town.trim()} className="h-9 rounded-full">Use town</Button>
            </div>
          </div>
        </div>
      )}

      {resolved && (
        <div data-testid={`${testid}-result`} className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
          <p>Found <b>{resolved.postcode}</b> · {resolved.admin_county || resolved.region || "UK"} · pin {resolved.lat?.toFixed(4)}, {resolved.lng?.toFixed(4)}</p>
          <Label className="mt-2 block text-xs">Confirm the town shown to creators (edit if needed)</Label>
          <div className="mt-1 flex gap-2">
            <Input data-testid={`${testid}-town`} value={town} onChange={(e) => setTown(e.target.value)} placeholder="Town" className="h-9" />
            <Button type="button" size="sm" data-testid={`${testid}-confirm`} onClick={confirm} disabled={!town.trim()} className="h-9 rounded-full bg-emerald-600 hover:bg-emerald-700">Use this location</Button>
          </div>
        </div>
      )}

      {confirmed && !resolved && !error && (
        <p data-testid={`${testid}-current`} className="mt-2 flex items-center gap-1 text-xs text-emerald-700">
          <CheckCircle2 className="h-3.5 w-3.5" /> Saved: <b>{value.town}</b>{value.county ? `, ${value.county}` : ""}
          {value.lat != null ? " · map pin set" : " · no map pin (manual)"}
        </p>
      )}
    </div>
  );
}
